import { z } from 'zod';

/**
 * Schema for the `orders.pendingChanges` jsonb column.
 *
 * Written by `createOrderForPendingChanges` (the attendee starts a self-edit
 * via the registration app), then read and applied by `applyPendingChanges`
 * after the corresponding payment lands. Sharing this schema keeps the
 * writer + reader from drifting silently — without it, a producer-side
 * shape change would only break at apply time, deep inside the post-payment
 * flow.
 */
export const pendingChangesFormValueSchema = z.object({
	formFieldId: z.string(),
	value: z.preprocess(
		(val) => (typeof val === 'number' ? String(val) : val),
		z.union([z.string(), z.array(z.string())]),
	),
	fieldOptionId: z.string().nullable().optional(),
});

// Discount preview computed at createOrderForPendingChanges time and consumed
// by applyPendingChanges. Stores enough information to update (or void) the
// existing discount line item on the original order when the payment lands.
export const pendingChangesDiscountPreviewSchema = z.object({
	discountCodeRedemptionId: z.string(),
	previousDiscountAmount: z.string(),
	projectedDiscountAmount: z.string(),
	// orderItemId → cents; null signals "rule no longer resolvable, preserve original amount"
	projectedAllocation: z.record(z.string(), z.number()).nullable(),
	ruleStillValid: z.boolean(),
	// Optional: absent on drafts stored before this field existed.
	projectedUnitsConsumed: z.number().nullable().optional(),
});

// Set when the ticket being changed belongs to a place in a group order. The
// buyer's payment stays where it is and the holder pays the difference, so the
// draft carries the place and the server-priced difference rather than letting
// apply time re-derive a number the customer never saw.
export const pendingChangesSwapSchema = z.object({
	slotId: z.string(),
	difference: z.number(),
});

export const pendingChangesSchema = z.object({
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	ticketId: z.string().optional(),
	registrationTypeId: z.string().nullable().optional(),
	formValues: z.array(pendingChangesFormValueSchema).optional(),
	addons: z.array(z.string()).optional(),
	sessionPackages: z.array(z.string()).optional(),
	discountPreviews: z.array(pendingChangesDiscountPreviewSchema).optional(),
	swap: pendingChangesSwapSchema.optional(),
});

export type PendingChanges = z.infer<typeof pendingChangesSchema>;
export type PendingChangesSwap = z.infer<typeof pendingChangesSwapSchema>;
export type PendingChangesDiscountPreview = z.infer<typeof pendingChangesDiscountPreviewSchema>;
